import sharedSupabase from '../config/supabase';

/**
 * Client for the `purchase-units` edge function — the server side of the unit
 * model.
 *
 * The browser owns no conversion table. It asks for a ladder once per session,
 * multiplies rungs locally while the counter types, and comes back here for
 * the things it genuinely cannot do: a measure in a unit it has no factor for,
 * a PAR preview, a package interpretation.
 *
 * Every call fails soft. Counting is the one workflow in this app that happens
 * on a phone in a walk-in with no signal, so an unreachable server must degrade
 * to "we cannot convert this yet", never to a thrown error that loses a count
 * or a zero that understates the shelf.
 *
 * Every function takes an optional trailing `client` (defaults to the shared
 * supabase singleton) so tests can inject a fake.
 */

const FUNCTION_NAME = 'purchase-units';

const messageFrom = (error, fallback) => {
  const text = String(error?.message || '').trim();
  return text || fallback;
};

/**
 * One invoke, with the failure shape every caller here returns.
 * `ok:false` is a normal outcome, not an exception.
 */
async function call(action, payload, client) {
  try {
    const { data, error } = await client.functions.invoke(FUNCTION_NAME, {
      body: { action, ...payload },
    });
    if (error) return { ok: false, error: messageFrom(error, 'Could not reach the server.'), data: null };
    if (data && data.success === false) {
      return { ok: false, error: messageFrom(data.error ? { message: data.error } : null, 'Request failed.'), data: null };
    }
    return { ok: true, error: null, data: data || {} };
  } catch (err) {
    return { ok: false, error: messageFrom(err, 'Could not reach the server.'), data: null };
  }
}

/**
 * The countable rungs for a set of items, keyed by inventory_item_id.
 *
 * Fetched once when a count session loads and cached with it — that is what
 * keeps package counting working offline.
 *
 * @returns {Promise<{ ok:boolean, laddersByItemId:Object, missing:Array, error:string|null }>}
 */
export async function fetchCountLadders(storeId, inventoryItemIds, client = sharedSupabase) {
  const ids = [...new Set((inventoryItemIds || []).filter((id) => id != null).map(String))];
  if (!storeId || ids.length === 0) {
    return { ok: true, laddersByItemId: {}, missing: [], error: null };
  }

  const { ok, error, data } = await call('count_ladder', { storeId, inventoryItemIds: ids }, client);
  if (!ok) return { ok: false, laddersByItemId: {}, missing: ids, error };

  const laddersByItemId = {};
  (data.items || []).forEach((entry) => {
    laddersByItemId[String(entry.inventory_item_id)] = {
      countUnit: entry.count_unit,
      rungs: entry.rungs || [],
      unresolvedOptions: entry.unresolved_options || [],
      optionCount: entry.option_count ?? 0,
    };
  });
  return { ok: true, laddersByItemId, missing: data.missing || [], error: null };
}

/**
 * What a typed PAR will actually be stored as.
 *
 * Shown live under the input so "2 tubs" visibly becomes "10 gal" — and so
 * "2 cases" visibly becomes a question about which vendor, rather than a
 * number that looks usable and is not.
 */
export async function previewParConversion(
  { storeId, inventoryItemId, value, unit, purchaseOptionId = null } = {},
  client = sharedSupabase,
) {
  const { ok, error, data } = await call(
    'preview_par_conversion',
    { storeId, inventoryItemId, value, unit, purchaseOptionId },
    client,
  );
  if (!ok) return { ok: false, preview: null, error };
  return { ok: true, preview: data.preview || null, error: null };
}

/**
 * The authoritative on-hand total for a set of raw entries.
 *
 * The browser already knows the package rungs; this is for measures in units it
 * has no factor for, and for the save path, where the server recomputes rather
 * than accepting a total it was handed.
 */
export async function normalizeEntries(
  { storeId, countItemId, entries } = {},
  client = sharedSupabase,
) {
  const { ok, error, data } = await call(
    'normalize_count_entries',
    { storeId, countItemId, entries: entries || [] },
    client,
  );
  if (!ok) return { ok: false, totals: null, error };
  return { ok: true, totals: data.totals || null, error: null };
}

/**
 * Need, packages, overage and MOQ for one count line — each labelled with the
 * unit it is measured in, because "10", "2" and "288" are three different
 * quantities that a column headed "Quantity" cannot tell apart.
 */
export async function previewOrderQuantities(
  { storeId, countItemId, purchaseOptionId = null } = {},
  client = sharedSupabase,
) {
  const { ok, error, data } = await call(
    'preview_order_quantities',
    { storeId, countItemId, purchaseOptionId },
    client,
  );
  if (!ok) return { ok: false, preview: null, error };
  return { ok: true, preview: data.preview || null, error: null };
}

/**
 * Reads a package description into structure: how many inner packs, how big
 * each one is, how much the whole package is worth in the item's count unit.
 *
 * Deterministic parsing runs first on the server and usually answers. The model
 * is only consulted for descriptions the parser cannot decompose, and whatever
 * it returns is recomputed there before it reaches this function.
 *
 * @returns {Promise<{ ok, interpretation, requiresReview, usedAi, unavailable,
 *                     rejected, message, error }>}
 *   `interpretation.quantity_in_item_count_unit` is null when the package could
 *   not be resolved. It is never 0.
 */
export async function interpretPurchasePackage(
  { storeId, purchaseOptionId } = {},
  client = sharedSupabase,
) {
  const { ok, error, data } = await call(
    'interpret_purchase_package',
    { storeId, purchaseOptionId },
    client,
  );
  if (!ok) {
    return {
      ok: false,
      interpretation: null,
      requiresReview: true,
      usedAi: false,
      unavailable: true,
      rejected: false,
      message: 'Package analysis is unavailable right now. Enter the package size manually.',
      error,
    };
  }
  return {
    ok: true,
    interpretation: data.interpretation || null,
    requiresReview: data.requires_review !== false,
    usedAi: data.used_ai === true,
    unavailable: data.ai_unavailable === true,
    rejected: data.rejected === true,
    trustDecision: data.trust_decision || (data.requires_review === false ? 'auto_accept' : null),
    priceConsistency: data.price_consistency || null,
    persisted: data.persisted === true,
    message: data.message || null,
    error: null,
  };
}

/**
 * Stores an interpretation a person has accepted.
 *
 * The server revalidates it even though it just produced it — a value that has
 * been through the browser is a proposal again — and writes it under
 * conversion_snapshot.package_interpretation, which the parser short-circuits
 * on so a confirmed package never changes because a parser improved.
 */
export async function confirmPurchasePackage(
  { storeId, purchaseOptionId, interpretation } = {},
  client = sharedSupabase,
) {
  const { ok, error, data } = await call(
    'confirm_purchase_package',
    { storeId, purchaseOptionId, interpretation },
    client,
  );
  if (!ok) return { ok: false, interpretation: null, error };
  return { ok: true, interpretation: data.interpretation || null, error: null };
}
